/* ------------------------------------------------------------------
   IZEN — HERITAGE real-Safari isolation harness. DIAGNOSTIC ONLY.

   Every branch below is gated on a `?hztest=` query parameter. The very
   first line reads it and returns if it is absent, so on a normal visit —
   the entire live site, every page, no query string — this file executes
   one URLSearchParams read and does nothing else: no class added, no
   console output, no GL call, no readback. It never touches the zipper's
   geometry, timing, scroll mapping, mesh or shader; it only (a) adds one
   class to <html> that assets/izen-hz-diag.css reads, and (b) once,
   read-only, reports what the existing WebGL context already contains.

   canvas.getContext('webgl') on a canvas that already has a context
   returns that SAME context, per spec, ignoring any options passed here —
   so this file never creates a second context or duplicates a GL resource.
   izenRenderAt() is the existing, already-shipped test hook (izen-core.js);
   calling it here paints one deterministic frame (closed, progress 0) so
   the pixel report below is reproducible, exactly as it was used for every
   prior diagnostic pass on this animation.

   Delete this file and its one <script> include in layout/theme.liquid to
   remove the harness entirely.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var variant = new URLSearchParams(location.search).get('hztest');
  if (!variant) return;

  var LABELS = {
    'baseline': 'BASELINE',
    'grain-off': 'GRAIN OFF',
    'washed-off': 'WASHED OFF',
    'flat-bg': 'FLAT BACKGROUND',
    'canvas-isolated': 'CANVAS ISOLATED',
    'gpu-layer': 'GPU LAYER',
    'srgb': 'SRGB',
    'canvas-hidden': 'CANVAS HIDDEN',
    'canvas-empty': 'CANVAS EMPTY',
    'primitive': 'PRIMITIVE',
    'body-only': 'BODY ONLY',
    'alpha-discard': 'ALPHA DISCARD',
    'canvas-half': 'CANVAS HALF'
  };
  var label = LABELS[variant];
  if (!label) return;

  document.documentElement.classList.add('hz-test-' + variant);
  // eslint-disable-next-line no-console
  console.log('IZEN HZ TEST: ' + label);

  /* Sample points as fractions of the canvas (u = left→right, v = bottom→top
     to match gl.readPixels' own origin). Derived from the measured garment
     bounding box in izen-hz-body.png (x 0.1188-0.8979, y 0.0271-0.9442 from
     the top), with margin, so every point below is outside the silhouette
     at every zip state — the panels only deform near the centre seam, the
     outer contour these points sit near is static. The two "Kapuzenkante"
     points straddle the measured hood-top edge at column 0.518. */
  var POINTS = {
    'oben links (aussen)': [0.05, 0.95],
    'oben rechts (aussen)': [0.95, 0.95],
    'links neben Hoodie': [0.03, 0.60],
    'rechts neben Hoodie': [0.97, 0.60],
    'unterhalb Hoodie': [0.50, 0.03],
    'Kapuzenkante aussen (~1px)': [0.518, 0.981],
    'Kapuzenkante innen (~1px)': [0.518, 0.965]
  };

  function p3Support() {
    try {
      return CSS.supports('color', 'color(display-p3 1 1 1)');
    } catch (e) {
      return 'CSS.supports warf einen Fehler: ' + e.message;
    }
  }

  function report() {
    var canvas = document.querySelector('[data-izen-hz-canvas]');
    if (!canvas) return;
    var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return;

    var colorSpace = {
      drawingBufferColorSpace: 'drawingBufferColorSpace' in gl ? gl.drawingBufferColorSpace : '(nicht unterstuetzt)',
      unpackColorSpace: 'unpackColorSpace' in gl ? gl.unpackColorSpace : '(nicht unterstuetzt)'
    };

    if (variant === 'srgb') {
      if ('drawingBufferColorSpace' in gl) {
        var before = gl.drawingBufferColorSpace;
        try {
          gl.drawingBufferColorSpace = 'srgb';
          colorSpace.srgbTest = (gl.drawingBufferColorSpace === 'srgb')
            ? 'gesetzt (Ausgangswert war bereits: ' + before + ')'
            : 'Zuweisung ignoriert, Wert blieb: ' + gl.drawingBufferColorSpace;
        } catch (e) {
          colorSpace.srgbTest = 'Exception beim Setzen: ' + e.message;
        }
      } else {
        colorSpace.srgbTest = 'Property nicht vorhanden';
        console.log('IZEN HZ TEST: SRGB UNSUPPORTED');
      }
    }

    // eslint-disable-next-line no-console
    console.log('IZEN HZ COLORSPACE ' + JSON.stringify({
      contextAttributes: gl.getContextAttributes(),
      colorSpace: colorSpace,
      displayP3Supported: p3Support(),
      devicePixelRatio: window.devicePixelRatio,
      userAgent: navigator.userAgent,
      platform: navigator.platform
    }, null, 1));

    var root = document.querySelector('[data-izen-hz]');
    if (root && typeof root.izenRenderAt === 'function') {
      root.izenRenderAt(0, 0);
    }

    var W = canvas.width, H = canvas.height;
    if (!W || !H) return;
    var px = new Uint8Array(4);
    var out = {};
    Object.keys(POINTS).forEach(function (name) {
      var u = POINTS[name][0], v = POINTS[name][1];
      var x = Math.min(W - 1, Math.max(0, Math.round(u * W)));
      var y = Math.min(H - 1, Math.max(0, Math.round(v * H)));
      gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
      out[name] = 'rgba(' + px[0] + ',' + px[1] + ',' + px[2] + ',' + px[3] + ') @ x=' + x + ' y=' + y;
    });
    // eslint-disable-next-line no-console
    console.log('IZEN HZ RAW PIXELS ' + JSON.stringify(out, null, 1));
  }

  /* Bounded poll, not a permanent loop: stops the moment izen-core.js has
     armed the renderer (its own 'is-live' class), or after ~10s if an asset
     never loads — matching izen-core.js's own fail() path, which leaves the
     closed plate on screen and never sets is-live. */
  function waitForLive(triesLeft) {
    var root = document.querySelector('[data-izen-hz]');
    if (root && root.classList.contains('is-live')) {
      report();
      return;
    }
    if (triesLeft <= 0) {
      console.log('IZEN HZ TEST: renderer never went live, no pixel report');
      return;
    }
    requestAnimationFrame(function () { waitForLive(triesLeft - 1); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForLive(600); });
  } else {
    waitForLive(600);
  }
})();
